import React from 'react';
import { ChartConfig, AdvancedConfig, ScientificOptions, VectorOptions, ThreeDOptions, MapOptions, KPICardConfig, DEFAULT_KPI_CONFIG } from '../../types';
import { Settings, Grid, Activity, Layers, Move, Maximize, Map as MapIcon, ArrowRight } from 'lucide-react';

import { ThreeDBarSettings } from '../../src/charts/ThreeDBarChart/ui/ThreeDBarSettings';
import { DEFAULT_3D_BAR_CONFIG } from '../../src/charts/ThreeDBarChart/config/threeDBarDefaults';
import { MapChartConfigPanel, MapChartConfig } from '../../src/charts/MapChart';
import { ForceGraphConfigPanel } from '../../src/charts/ForceGraph/ui/ForceGraphConfigPanel';
import { ScientificControls } from '../ScientificControls';

interface AdvancedSettingsProps {
    config: ChartConfig;
    onChange: (newConfig: AdvancedConfig) => void;
    onConfigChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
    allCols: string[];
    numericCols: string[];
}

export const AdvancedSettings: React.FC<AdvancedSettingsProps> = ({ config, onChange, onConfigChange, allCols, numericCols }) => {
    const advanced = config.advanced || {};

    const updateAdvanced = (section: keyof AdvancedConfig, key: string, value: any) => {
        onChange({
            ...advanced,
            [section]: {
                ...advanced[section] as any,
                [key]: value
            }
        });
    };

    const renderScientificSettings = () => {
        return (
            <ScientificControls
                config={config}
                onChange={(newSciConfig) => onChange({
                    ...advanced,
                    scientific: newSciConfig
                })}
                onConfigChange={onConfigChange}
                allCols={allCols}
                numericCols={numericCols}
            />
        );
    };

    const renderVectorSettings = () => {
        const vec = advanced.vector || {
            uField: null,
            vField: null,
            arrowScaling: 1,
            normalize: false
        };

        return (
            <div className="space-y-4">
                {/* Vector Fields moved to Left Sidebar */}
                <div className="space-y-2">
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Arrow Scaling: {vec.arrowScaling}x</label>
                        <input
                            type="range"
                            min="0.1"
                            max="5"
                            step="0.1"
                            value={vec.arrowScaling}
                            onChange={(e) => updateAdvanced('vector', 'arrowScaling', parseFloat(e.target.value))}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                            type="checkbox"
                            checked={vec.normalize}
                            onChange={(e) => updateAdvanced('vector', 'normalize', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Normalize Vectors
                    </label>
                </div>
            </div>
        );
    };

    const render3DSettings = () => {
        const td = advanced.threeD || {
            xField: null,
            yField: null,
            zField: null,
            pointSize: 4
        };

        return (
            <div className="space-y-4">
                {/* 3D Mapping moved to Left Sidebar */}
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Point Size: {td.pointSize}</label>
                    <input
                        type="range"
                        min="1"
                        max="20"
                        value={td.pointSize}
                        onChange={(e) => updateAdvanced('threeD', 'pointSize', parseInt(e.target.value))}
                        className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                    />
                </div>
            </div>
        );
    };

    const renderMapSettings = () => {
        const map = advanced.map || {
            latField: null,
            lonField: null,
            geoMode: 'points'
        };

        // Get or init map config
        const mapConfig = (advanced.mapConfig || {}) as MapChartConfig;

        return (
            <div className="space-y-4">
                {/* Map Coordinates moved to Left Sidebar */}
                <div className="pt-4 border-t border-white/10">
                    <MapChartConfigPanel
                        config={mapConfig}
                        onChange={(newConfig) => onChange({
                            ...advanced,
                            mapConfig: newConfig
                        })}
                    />
                </div>
            </div>
        );
    };

    const renderForceGraphSettings = () => {
        const forceConfig = advanced.forceGraph || {};
        return (
            <ForceGraphConfigPanel
                config={forceConfig as any}
                onChange={(newConfig) => onChange({
                    ...advanced,
                    forceGraph: {
                        ...forceConfig,
                        ...newConfig
                    }
                })}
                allCols={allCols}
                chartConfig={config}
                onChartConfigChange={onConfigChange as any}
            />
        );
    };

    const renderKPICardSettings = () => {
        const kpi: KPICardConfig = { ...DEFAULT_KPI_CONFIG, ...advanced.kpiCard };

        return (
            <div className="space-y-4">
                <h3 className="text-xs font-bold text-neon-gold/80 uppercase tracking-widest flex items-center gap-2">
                    <Settings className="w-3.5 h-3.5" />
                    KPI Card Settings
                </h3>

                {/* Value Format */}
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Value Format</label>
                    <select
                        value={kpi.valueFormat}
                        onChange={(e) => updateAdvanced('kpiCard', 'valueFormat', e.target.value)}
                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                    >
                        <option value="plain">Plain Number</option>
                        <option value="compact">Compact (1.2K, 3.4M)</option>
                        <option value="currency">Currency</option>
                        <option value="percent">Percentage</option>
                    </select>
                </div>

                {kpi.valueFormat === 'currency' && (
                    <div>
                        <label className="text-xs text-gray-400 block mb-1">Currency Symbol</label>
                        <input
                            type="text"
                            value={kpi.currencySymbol}
                            onChange={(e) => updateAdvanced('kpiCard', 'currencySymbol', e.target.value)}
                            className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                            placeholder="$"
                        />
                    </div>
                )}

                <div>
                    <label className="text-xs text-gray-400 block mb-1">Decimal Places</label>
                    <input
                        type="number"
                        min="0"
                        max="6"
                        value={kpi.decimalPlaces}
                        onChange={(e) => updateAdvanced('kpiCard', 'decimalPlaces', parseInt(e.target.value) || 0)}
                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200"
                    />
                </div>

                {/* Card Size */}
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Card Size</label>
                    <select
                        value={kpi.cardSize}
                        onChange={(e) => updateAdvanced('kpiCard', 'cardSize', e.target.value)}
                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                    >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                    </select>
                </div>

                {/* Alignment */}
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Alignment</label>
                    <div className="flex gap-2">
                        {(['left', 'center'] as const).map((align) => (
                            <button
                                key={align}
                                onClick={() => updateAdvanced('kpiCard', 'alignment', align)}
                                className={`flex-1 py-2 rounded-lg text-sm capitalize transition-all ${kpi.alignment === align
                                        ? 'bg-neon-gold/20 text-neon-gold border border-neon-gold/30'
                                        : 'bg-obsidian-800 text-gray-400 border border-white/10 hover:border-white/20'
                                    }`}
                            >
                                {align}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="h-px bg-white/10" />

                {/* Display Toggles */}
                <div className="space-y-2">
                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={kpi.showDelta}
                            onChange={(e) => updateAdvanced('kpiCard', 'showDelta', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Show Delta (vs comparison)
                    </label>

                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={kpi.showTrendline}
                            onChange={(e) => updateAdvanced('kpiCard', 'showTrendline', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Show Sparkline Trend
                    </label>

                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={kpi.showIcon}
                            onChange={(e) => updateAdvanced('kpiCard', 'showIcon', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Show Icon
                    </label>

                    <label className="flex items-center gap-3 text-sm text-gray-300 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={kpi.enableClickToFilter}
                            onChange={(e) => updateAdvanced('kpiCard', 'enableClickToFilter', e.target.checked)}
                            className="rounded border-gray-600 bg-obsidian-800 text-gold-500 focus:ring-gold-500/50"
                        />
                        Enable Click to Filter
                    </label>
                </div>

                <div className="h-px bg-white/10" />

                {/* Delta Mode */}
                <div>
                    <label className="text-xs text-gray-400 block mb-1">Delta Logic</label>
                    <select
                        value={kpi.deltaMode}
                        onChange={(e) => updateAdvanced('kpiCard', 'deltaMode', e.target.value)}
                        className="w-full text-sm px-3 py-2 bg-obsidian-800 border border-gold-500/20 rounded-lg text-gray-200 focus:ring-2 focus:ring-gold-500/50"
                    >
                        <option value="higher-is-better">Higher is Better (green for +)</option>
                        <option value="lower-is-better">Lower is Better (green for -)</option>
                    </select>
                </div>
            </div>
        );
    };




    return (
        <div className="space-y-6 p-4 bg-obsidian-900/50 rounded-xl border border-gold-500/10">
            {config.type === 'scientific' && renderScientificSettings()}
            {config.type === 'vector' && renderVectorSettings()}
            {config.type === '3d' && render3DSettings()}
            {config.type === '3d-bar' && (
                <ThreeDBarSettings
                    config={{ ...DEFAULT_3D_BAR_CONFIG, ...(config.advanced?.threeDBar || {}) }}
                    onChange={(updates) => {
                        onChange({
                            ...advanced,
                            threeDBar: {
                                ...DEFAULT_3D_BAR_CONFIG,
                                ...(advanced.threeDBar || {}),
                                ...updates
                            }
                        });
                    }}
                    allCols={allCols}
                    numericCols={numericCols}
                    chartConfig={config}
                    onChartConfigChange={onConfigChange as any}
                />
            )}
            {config.type === 'map' && renderMapSettings()}

            {config.type === 'force-directed' && renderForceGraphSettings()}
            {config.type === 'kpi-card' && renderKPICardSettings()}
        </div>
    );
};
